import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Download, X } from 'lucide-react';

import { api } from '../lib/api.ts';
import { baixar } from '../lib/exportar.ts';
import { Aviso, Botao, Campo, Carregando, Cartao, Etiqueta, Selecao, Tabela } from '../componentes/ui.tsx';

interface Evento {
  id: string;
  ocorridoEm: string;
  usuario: string | null;
  acao: string;
  entidade: string | null;
  entidadeId: string | null;
  antes: unknown;
  depois: unknown;
  detalhe: string | null;
  ip: string | null;
}

/**
 * Rótulos legíveis para as ações.
 *
 * O nome técnico continua guardado no banco — é ele que serve para filtrar e
 * para procurar no código. Aqui é só o que a pessoa lê.
 */
const ROTULOS: Record<string, { texto: string; tom: string }> = {
  'usuario.bootstrap': { texto: 'Criou o primeiro administrador', tom: 'medio' },
  'usuario.criar': { texto: 'Criou usuário', tom: 'neutro' },
  'usuario.editar': { texto: 'Alterou perfil ou situação de usuário', tom: 'medio' },
  'usuario.redefinir-senha': { texto: 'Redefiniu a senha de outra pessoa', tom: 'ruim' },
  'usuario.trocar-propria-senha': { texto: 'Trocou a própria senha', tom: 'neutro' },
  'usuario.editar-proprio-nome': { texto: 'Alterou o próprio nome', tom: 'neutro' },

  'fechamento.fechar': { texto: 'Fechou a competência', tom: 'bom' },
  'fechamento.reabrir': { texto: 'Reabriu a competência', tom: 'ruim' },

  'regras.versao.criar': { texto: 'Criou versão de regras', tom: 'neutro' },
  'regras.versao.editar': { texto: 'Editou a versão de regras', tom: 'medio' },
  'regras.versao.ativar': { texto: 'Ativou versão de regras', tom: 'ruim' },
  'regras.versao.excluir': { texto: 'Excluiu versão de regras', tom: 'ruim' },
  'regras.faixas.substituir': { texto: 'Alterou faixas de premiação', tom: 'ruim' },
  'regras.balizador.substituir': { texto: 'Alterou faixas do balizador', tom: 'ruim' },
  'regras.parametros.editar': { texto: 'Alterou pisos e parâmetros', tom: 'ruim' },
  'regras.filtro.editar': { texto: 'Alterou como um status conta', tom: 'medio' },

  'funcionario.excluir-do-mes': { texto: 'Tirou funcionário do fechamento', tom: 'medio' },
  'funcionario.incluir-no-mes': { texto: 'Devolveu funcionário ao fechamento', tom: 'neutro' },
  'funcionario.provisorio.cadastrar': { texto: 'Cadastrou funcionário provisório', tom: 'neutro' },
  'funcionario.provisorio.conciliar': { texto: 'Conciliou cadastros provisórios', tom: 'neutro' },
  'cargo.mapeamento.criar': { texto: 'Criou mapeamento de cargo', tom: 'medio' },
  'cargo.mapeamento.encerrar': { texto: 'Encerrou mapeamento de cargo', tom: 'medio' },
  'cargo.mapeamento.excluir': { texto: 'Excluiu mapeamento de cargo', tom: 'medio' },
};

function rotulo(acao: string): { texto: string; tom: string } {
  return ROTULOS[acao] ?? { texto: acao.replace(/[.\-_]/g, ' '), tom: 'neutro' };
}

function dataHora(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

const LIMITE = 50;

export function Auditoria() {
  const [acao, setAcao] = useState('');
  const [usuario, setUsuario] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [busca, setBusca] = useState('');
  const [salto, setSalto] = useState(0);
  const [aberto, setAberto] = useState<string | null>(null);

  const filtros = useMemo(() => {
    const p = new URLSearchParams();
    if (acao !== '') p.set('acao', acao);
    if (usuario !== '') p.set('usuario', usuario);
    if (de !== '') p.set('de', de);
    if (ate !== '') p.set('ate', ate);
    if (busca !== '') p.set('busca', busca);
    p.set('limite', String(LIMITE));
    p.set('salto', String(salto));
    return p.toString();
  }, [acao, usuario, de, ate, busca, salto]);

  const opcoes = useQuery({
    queryKey: ['auditoria', 'acoes'],
    queryFn: () =>
      api<{ acoes: { acao: string; total: number }[]; usuarios: string[] }>('/auditoria/acoes'),
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['auditoria', filtros],
    queryFn: () =>
      api<{ total: number; limite: number; salto: number; eventos: Evento[] }>(
        `/auditoria?${filtros}`,
      ),
  });

  const filtrando = acao !== '' || usuario !== '' || de !== '' || ate !== '' || busca !== '';

  if (isError) {
    return (
      <Aviso tom="erro" titulo="Não foi possível carregar o log">
        <p>{error.message}</p>
      </Aviso>
    );
  }

  return (
    <div className="space-y-6">
      <Aviso tom="info" titulo="O que este log é">
        <p>
          Todo registro é gravado no momento da ação e <strong>nunca é alterado nem apagado</strong> —
          não existe rota de escrita para esta tabela. É o que responde "quem mudou essa faixa" ou
          "quem reabriu o mês" meses depois.
        </p>
        <p>Visível só para administradores, porque mostra a ação de todos os usuários.</p>
      </Aviso>

      <Cartao
        titulo="Filtros"
        acoes={
          filtrando && (
            <Botao
              tipo="secundario"
              onClick={() => {
                setAcao('');
                setUsuario('');
                setDe('');
                setAte('');
                setBusca('');
                setSalto(0);
              }}
            >
              <X className="size-4" />
              Limpar
            </Botao>
          )
        }
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Selecao
            rotulo="Ação"
            valor={acao}
            onChange={(v) => {
              setAcao(v);
              setSalto(0);
            }}
            opcoes={(opcoes.data?.acoes ?? []).map((a) => a.acao)}
            vazio="Todas as ações"
          />
          <Selecao
            rotulo="Usuário"
            valor={usuario}
            onChange={(v) => {
              setUsuario(v);
              setSalto(0);
            }}
            opcoes={opcoes.data?.usuarios ?? []}
            vazio="Todos"
          />
          <Campo rotulo="De" tipo="date" valor={de} onChange={(v) => { setDe(v); setSalto(0); }} />
          <Campo rotulo="Até" tipo="date" valor={ate} onChange={(v) => { setAte(v); setSalto(0); }} />
          <Campo
            rotulo="Buscar"
            valor={busca}
            onChange={(v) => {
              setBusca(v);
              setSalto(0);
            }}
          />
        </div>
        {acao !== '' && (
          <p className="mt-3 text-xs text-slate-500">
            Nome técnico da ação: <code className="font-mono">{acao}</code>
          </p>
        )}
      </Cartao>

      {isLoading && <Carregando />}

      {data !== undefined && (
        <Cartao
          titulo={`${data.total} registro(s)`}
          descricao={
            data.total > LIMITE
              ? `Mostrando ${data.salto + 1} a ${Math.min(data.salto + LIMITE, data.total)}.`
              : undefined
          }
          acoes={
            <Botao
              tipo="secundario"
              desabilitado={data.eventos.length === 0}
              onClick={() =>
                baixar(
                  'auditoria.csv',
                  [
                    ['Quando', 'Usuário', 'Ação', 'Entidade', 'Referência', 'Detalhe', 'IP'].join(';'),
                    ...data.eventos.map((e) =>
                      [
                        dataHora(e.ocorridoEm),
                        e.usuario ?? '',
                        rotulo(e.acao).texto,
                        e.entidade ?? '',
                        e.entidadeId ?? '',
                        (e.detalhe ?? '').replace(/;/g, ','),
                        e.ip ?? '',
                      ].join(';'),
                    ),
                  ].join('\r\n'),
                )
              }
            >
              <Download className="size-4" />
              Exportar esta página
            </Botao>
          }
        >
          {data.eventos.length === 0 ? (
            <p className="py-6 text-sm text-slate-500">Nenhum registro com esses filtros.</p>
          ) : (
            <>
              <Tabela
                cabecalho={
                  <tr>
                    <th className="py-2 pr-3">Quando</th>
                    <th className="py-2 pr-3">Quem</th>
                    <th className="py-2 pr-3">O que fez</th>
                    <th className="py-2 pr-3">Referência</th>
                    <th className="py-2 pr-3">Detalhe</th>
                  </tr>
                }
              >
                {data.eventos.map((e) => {
                  const r = rotulo(e.acao);
                  const temDiff = e.antes !== null || e.depois !== null;
                  return [
                    <tr key={e.id} className="hover:bg-slate-50">
                      <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                        {temDiff ? (
                          <button
                            onClick={() => setAberto(aberto === e.id ? null : e.id)}
                            className="inline-flex items-center gap-1 hover:underline"
                          >
                            {aberto === e.id ? (
                              <ChevronDown className="size-3.5 shrink-0 text-slate-400" />
                            ) : (
                              <ChevronRight className="size-3.5 shrink-0 text-slate-400" />
                            )}
                            {dataHora(e.ocorridoEm)}
                          </button>
                        ) : (
                          <span className="pl-4.5">{dataHora(e.ocorridoEm)}</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">{e.usuario ?? '—'}</td>
                      <td className="py-2 pr-3">
                        <Etiqueta tom={r.tom}>{r.texto}</Etiqueta>
                      </td>
                      <td className="py-2 pr-3 text-xs text-slate-500">
                        {e.entidade ?? '—'}
                        {e.entidadeId !== null && (
                          <span className="ml-1 font-mono">{e.entidadeId.slice(0, 20)}</span>
                        )}
                      </td>
                      <td className="max-w-96 py-2 pr-3 text-slate-600">{e.detalhe ?? '—'}</td>
                    </tr>,
                    aberto === e.id ? (
                      <tr key={`${e.id}-diff`} className="bg-slate-50">
                        <td colSpan={5} className="px-4 py-3">
                          <Diferenca antes={e.antes} depois={e.depois} />
                          {e.ip !== null && (
                            <p className="mt-3 font-mono text-[11px] text-slate-400">IP {e.ip}</p>
                          )}
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </Tabela>

              {data.total > LIMITE && (
                <div className="mt-5 flex items-center gap-3">
                  <Botao
                    tipo="secundario"
                    desabilitado={salto === 0}
                    onClick={() => setSalto(Math.max(0, salto - LIMITE))}
                  >
                    Anteriores
                  </Botao>
                  <Botao
                    tipo="secundario"
                    desabilitado={salto + LIMITE >= data.total}
                    onClick={() => setSalto(salto + LIMITE)}
                  >
                    Próximos
                  </Botao>
                </div>
              )}
            </>
          )}
        </Cartao>
      )}
    </div>
  );
}

/**
 * Mostra o antes e o depois campo por campo.
 *
 * A comparação chave por chave é o que torna o log útil de verdade: ver que o
 * fator do balizador foi de 0,75 para 0,9 diz mais do que dois blocos de JSON
 * lado a lado.
 */
function Diferenca({ antes, depois }: { antes: unknown; depois: unknown }) {
  const objeto = (v: unknown): Record<string, unknown> | null =>
    v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

  const a = objeto(antes);
  const d = objeto(depois);

  if (a === null && d === null) {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        {antes !== null && <Bloco titulo="Antes" valor={antes} />}
        {depois !== null && <Bloco titulo="Depois" valor={depois} />}
      </div>
    );
  }

  const chaves = [...new Set([...Object.keys(a ?? {}), ...Object.keys(d ?? {})])];

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200 text-left font-mono text-[10px] tracking-wider text-slate-500 uppercase">
          <th className="pb-1.5">Campo</th>
          <th className="pb-1.5">Antes</th>
          <th className="pb-1.5">Depois</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200">
        {chaves.map((k) => {
          const va = JSON.stringify(a?.[k] ?? null);
          const vd = JSON.stringify(d?.[k] ?? null);
          const mudou = va !== vd;
          return (
            <tr key={k} className={mudou ? '' : 'text-slate-400'}>
              <td className="py-1.5 pr-4 font-mono text-xs">{k}</td>
              <td className="py-1.5 pr-4 font-mono text-xs break-all">{va}</td>
              <td
                className={
                  mudou
                    ? 'py-1.5 font-mono text-xs font-semibold break-all text-slate-900'
                    : 'py-1.5 font-mono text-xs break-all'
                }
              >
                {vd}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Bloco({ titulo, valor }: { titulo: string; valor: unknown }) {
  return (
    <div>
      <p className="mb-1 font-mono text-[10px] tracking-wider text-slate-500 uppercase">{titulo}</p>
      <pre className="overflow-x-auto rounded border border-slate-200 bg-white p-2 text-xs">
        {JSON.stringify(valor, null, 2)}
      </pre>
    </div>
  );
}
